"use client"

import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  IconButton,
  useTheme,
  useMediaQuery,
  Stack,
  styled
} from "@mui/material";
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon
} from '@mui/icons-material';
import { useDeals } from "app/providers/deals-provider";

// Styled components following the image design
const CategoryButton = styled(Button)(({ theme, isActive }) => ({
  height: '32px',
  borderRadius: '8px',
  padding: '6px 12px',
  minWidth: 'auto',
  whiteSpace: 'nowrap',
  textTransform: 'none',
  fontSize: '13px',
  fontWeight: 400,
  backgroundColor: isActive ? 'var(--theme-dark, #2B4D59)' : 'transparent',
  color: isActive ? '#FFF' : 'var(--theme-dark, #2B4D59)',
  border: '1px solid',
  borderColor: isActive ? 'var(--theme-color, #436B78)' : 'var(--theme-dark, #2B4D59)',
  '&:hover': {
    backgroundColor: isActive ? 'var(--theme-dark, #2B4D59)' : 'rgba(67, 107, 120, 0.1)',
  },
  '&:disabled': {
    opacity: 0.3,
    cursor: 'not-allowed',
  }
}));

const NavigationButton = styled(IconButton)(({ theme }) => ({
  width: '32px',
  height: '32px',
  borderRadius: '8px',
  border: '1px solid var(--theme-color, #436B78)',
  color: 'var(--theme-color, #436B78)',
  backgroundColor: 'transparent',
  padding: '4px',
  '&:hover': {
    backgroundColor: 'rgba(67, 107, 120, 0.1)',
  },
  '&:disabled': {
    opacity: 0.3,
    cursor: 'not-allowed',
  }
}));

const ScrollContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  overflowX: 'auto',
  scrollbarWidth: 'none',
  msOverflowStyle: 'none',
  flex: 1,
  padding: '0 4px',
  '&::-webkit-scrollbar': {
    display: 'none',
  }
}));

const CategoryChips = () => {
  const { collectionCategories, selectedCollectionCategory, setSelectedCollectionCategory, isDealLoading, page,
    filters } = useDeals();
  const scrollContainerRef = useRef(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const checkOverflow = () => {
    if (scrollContainerRef.current) {
      const { scrollWidth, clientWidth, scrollLeft } = scrollContainerRef.current;
      setShowLeftArrow(scrollLeft > 0);
      setShowRightArrow(scrollWidth > clientWidth + scrollLeft);
    }
  };

  const scroll = (direction) => {
    if (scrollContainerRef.current) {
      const { scrollLeft, clientWidth } = scrollContainerRef.current;
      const scrollAmount = clientWidth / 2;
      const newScrollLeft = direction === "left"
        ? scrollLeft - scrollAmount
        : scrollLeft + scrollAmount;

      scrollContainerRef.current.scrollTo({
        left: newScrollLeft,
        behavior: "smooth"
      });
    }
  };

  useEffect(() => {
    checkOverflow();
    const handleResize = () => checkOverflow();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [selectedCollectionCategory, collectionCategories]);

  // Hide if no categories available or on mobile (mobile uses filter popover instead)
  if (!collectionCategories || !collectionCategories.length) {
    return null;
  }

  return (
    <Box sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 1.5,
      mb: 2,
      width: '100%',
      position: 'relative',
      px: 1
    }}>
      {showLeftArrow && (
        <NavigationButton
          onClick={() => scroll("left")}
          disabled={isDealLoading}
          size="medium"
          sx={{ borderRadius: '100px' }}
        >
          <ChevronLeftIcon />
        </NavigationButton>
      )}

      <ScrollContainer
        ref={scrollContainerRef}
        onScroll={checkOverflow}
      >
        <Stack direction="row" spacing={1.5} sx={{ flexWrap: 'nowrap' }}>
          {collectionCategories.map((category, index) => {
            const isActive = category.value === selectedCollectionCategory?.value;
            return (
              <CategoryButton
                key={index}
                onClick={() => setSelectedCollectionCategory(prev => prev?.value == category.value ? null : category)}
                disabled={isDealLoading}
                isActive={isActive}
              >
                {category.label}
              </CategoryButton>
            );
          })}
        </Stack>
      </ScrollContainer>

      {showRightArrow && (
        <NavigationButton
          onClick={() => scroll("right")}
          disabled={isDealLoading}
          size="medium"
          sx={{ borderRadius: '100px' }}
        >
          <ChevronRightIcon />
        </NavigationButton>
      )}
    </Box>
  );
};

export default CategoryChips;